import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import { ReportExpandedViewTypesEnum } from '../../../../report-data/report-data.data';
import { ReportDataStore } from '../../../../report-data/report-data.store';
import { Observable } from 'rxjs';
import { ExpandedSummary } from '../../../../report-data/models/expanded-summary.model';
import { filter, map, shareReplay, withLatestFrom } from 'rxjs/operators';
import { List } from 'immutable';
import { ExpandedViewItem } from '../../../../report-data/models/sub-models/expanded-view-item.model';
import { BarChart } from '../../../../charts/bar-chart.model';
import { LocalizationService } from '../../../../localization/localization.service';
import { MoneyPipe } from '../../../../shared/pipes/money/money.pipe';
import { TrackingScreenNamesEnum } from '../../../../tracking/tracking.data';
import { TrackingObjectBuilder } from '../../../../tracking/tracking-object.builder';
import { ReportExpandedTrackingObject } from '../../../../tracking/models/objects/report-expanded-module.model';
import { IRouter } from 'i2g-ng-ui';
import { InsightsSummaryBuilder } from '../../../builders';
import { ITrackingProperties } from 'i2g-ng-tracking';

@Component({
  selector: 'app-insights-summary-card',
  templateUrl: './insights-summary-card.component.html',
  styleUrls: ['./insights-summary-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [MoneyPipe],
})
export class InsightsSummaryCardComponent implements OnInit {
  @Input() reportType: ReportExpandedViewTypesEnum;
  @Input() trackingScreenName: TrackingScreenNamesEnum;
  @Input() trackingAdditionalProperties: ITrackingProperties;

  locale: string;

  summaryData$: Observable<ExpandedSummary>;
  summaryDataItems$: Observable<List<ExpandedViewItem>>;
  showSummaryAverage$: Observable<boolean>;
  summaryCtaRoute$: Observable<IRouter>;
  showEmptyState$: Observable<boolean>;
  emptyStateCta$: Observable<IRouter>;

  barChartContent$: Observable<BarChart>;

  trackingObject: ReportExpandedTrackingObject;

  constructor(
    private _reportDataStore: ReportDataStore,
    private _insightsSummaryBuilder: InsightsSummaryBuilder,
    private _localizationService: LocalizationService,
    private _trackingObjectBuilder: TrackingObjectBuilder
  ) {}

  ngOnInit() {
    this.locale = this._localizationService.getLocale();

    this.summaryData$ = this._reportDataStore
      .getExpandedSummaryDataByType(this.reportType)
      .pipe(shareReplay(1));

    this.summaryDataItems$ = this._reportDataStore
      .getExpandedViewItemsByType(this.reportType)
      .pipe(shareReplay(1));

    /**
     * If average calculation is $0 when we don’t have data for more than 1 month, the copy should be hidden
     */
    this.showSummaryAverage$ = this.summaryDataItems$.pipe(
      map((summaryDataItems: List<ExpandedViewItem>) =>
        summaryDataItems.reduce(
          (sum: number, item: ExpandedViewItem) => sum + item.quantity,
          0
        )
      ),
      withLatestFrom(this.summaryData$),
      map(([summaryItemsCount, summaryData]: [number, ExpandedSummary]) => {
        return (
          summaryData.barGraph &&
          !(summaryItemsCount <= 1 && summaryData.barGraph.quantity === 0)
        );
      })
    );

    this.summaryCtaRoute$ = this.summaryData$.pipe(
      map((summaryData) =>
        this._insightsSummaryBuilder.buildSummaryCtaRoute(
          summaryData,
          this.trackingScreenName
        )
      )
    );

    this.barChartContent$ = this.summaryData$.pipe(
      withLatestFrom(this.summaryDataItems$),
      filter(
        ([summaryData, summaryDataItems]: [
          ExpandedSummary,
          List<ExpandedViewItem>
        ]) => {
          return summaryData.barGraph && summaryDataItems.size > 0;
        }
      ),
      map(
        ([summaryData, summaryDataItems]: [
          ExpandedSummary,
          List<ExpandedViewItem>
        ]) =>
          this._insightsSummaryBuilder.buildBarChart(
            summaryData,
            summaryDataItems,
            this.locale
          )
      )
    );

    this.trackingObject = this._trackingObjectBuilder.buildReportExpanded(
      this.reportType
    );

    /**
     * Empty state
     */
    this.showEmptyState$ = this.summaryData$.pipe(
      map((summaryData) =>
        this._insightsSummaryBuilder.isEmptySummary(summaryData)
      )
    );

    this.emptyStateCta$ = this.summaryData$.pipe(
      map((summaryData) =>
        this._insightsSummaryBuilder.buildEmptyStateCtaRoute(
          summaryData,
          this.trackingScreenName
        )
      )
    );
  }
}
